import { AnimationEvent } from '@angular/animations';
import { Location } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    Inject,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { getBuildIdQuery } from '@ncg/data';
import { Subject } from 'rxjs';
import { distinctUntilChanged, take, takeUntil } from 'rxjs/operators';
import SwiperCore, { Keyboard, Lazy, Navigation, Pagination, Swiper, Thumbs } from 'swiper';
import { TrackingService } from '../../../core/tracking.service';

import { IProductGalleryItem } from '../../../models/product-gallery.interface';
import { ImpactOverlayRef } from '../../../overlay/overlay-ref';
import { ImpactOverlayLeave } from '../../../overlay/overlay.interfaces';
import { IMPACT_OVERLAY_DATA } from '../../../overlay/overlay.tokens';
import { slideInBottom } from '../../../utils/animations/slide.animations';

SwiperCore.use([Navigation, Pagination, Thumbs, Lazy, Keyboard]);

@Component({
    selector: 'ncg-product-gallery-carousel',
    template: `
        <div
            class="product-gallery-carousel carousel__overlay-background"
            [@slideInBottom]="animationState"
            (@slideInBottom.start)="onAnimationStart($event)"
            (@slideInBottom.done)="onAnimationDone($event)"
        >
            <!-- Close overlay -->
            <button
                aria-hidden="true"
                aria-label="Close gallery overlay"
                class="button is-clean product-gallery-carousel__close-btn"
                (click)="onClose()"
            >
                <svg-icon-sprite
                    [src]="'assets/images/sprite-multi.svg' + buildIdQuery + '#close-circle'"
                    [viewBox]="'0 0 58 58'"
                    [width]="'50px'"
                    [height]="'50px'"
                    aria-hidden="true"
                ></svg-icon-sprite>
            </button>

            <div class="product-gallery-carousel__content">
                <!-- Gallery -->
                <div class="product-gallery-carousel__container">
                    <div class="product-gallery-swiper swiper">
                        <div class="swiper-wrapper">
                            <div *ngFor="let item of media; let isFirst = first" class="swiper-slide level level-item" [ngSwitch]="item.type">
                                <figure *ngSwitchCase="'image'">
                                    <img *ngIf="isFirst" [src]="item.src | productImage: 'xLarge'" sizes="100vw" loading="lazy" ncgImageLoad />
                                    <img *ngIf="!isFirst" [attr.data-src]="item.src | productImage: 'xLarge'" class="swiper-lazy" />
                                </figure>

                                <figure [class.product-gallery-carousel__video]="hasConsent" *ngSwitchCase="'video'">
                                    <iframe
                                        *ngIf="hasConsent"
                                        [src]="item.src | safe: 'resourceUrl'"
                                        class="product-gallery-carousel__video--iframe swiper-lazy"
                                        allowfullscreen="true"
                                        allowtransparency="true"
                                        frameborder="0"
                                        title="Video player"
                                    ></iframe>

                                    <div *ngIf="!hasConsent" class="is-relative">
                                        <img *ngIf="item.thumbnail" class="product-gallery-carousel__image" [src]="item.thumbnail" ncgImageLoad />
                                        <div class="product-gallery__video-blocked">
                                            <div class="product-details__video-blocked-container">
                                                <p class="pb-3 has-text-centered">{{ 'embed_spot.video_blocked_placeholder' | translate }}</p>
                                                <button class="button is-primary" (click)="onShowCookieBanner()" type="button">
                                                    {{ 'embed_spot.open_cookie' | translate }}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </figure>
                            </div>
                        </div>
                        <div *ngIf="media.length > 1" class="is-hidden-tablet swiper-pagination"></div>
                    </div>
                </div>
                <div class="swiper-button-container product-gallery-carousel__button-container is-hidden-mobile">
                    <div
                        class="product-gallery-carousel__button product-gallery-carousel__button--prev swiper-button swiper-button-prev has-border"
                    ></div>
                    <div
                        class="product-gallery-carousel__button product-gallery-carousel__button--next swiper-button swiper-button-next has-border"
                    ></div>
                </div>
            </div>
            <div class="product-gallery-carousel__footer container is-full is-hidden-mobile">
                <!-- Thumbs -->
                <div class="gallery-overlay__thumbs swiper">
                    <div class="swiper-wrapper">
                        <div *ngFor="let item of media; let index = index" class="swiper-slide gallery-overlay__thumb">
                            <picture class="swiper-slide__thumb is-ratio-product-item" [class.is-video]="item.type === 'video'">
                                <img [src]="item.thumbnail | productImage: 'xSmall'" />
                            </picture>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [slideInBottom],
})
export class ProductGalleryCarouselComponent implements OnInit, AfterViewInit, OnDestroy, ImpactOverlayLeave {
    media: IProductGalleryItem[] = [];
    index = 0;
    gallerySwiper: Swiper;
    thumbsSwiper: Swiper;
    hasConsent = false;
    animationState: 'void' | 'enter' | 'leave' = 'enter';
    animationStateChanged = new EventEmitter<AnimationEvent>();
    buildIdQuery = getBuildIdQuery();

    private readonly unsubscribe = new Subject<void>();

    constructor(
        public dialogRef: ImpactOverlayRef,
        @Inject(IMPACT_OVERLAY_DATA) public data: any,
        private readonly trackingService: TrackingService,
        private readonly cd: ChangeDetectorRef,
        private readonly router: Router,
        private readonly location: Location
    ) {}

    onAnimationStart(event: AnimationEvent) {
        this.animationStateChanged.emit(event);
    }

    onAnimationDone(event: AnimationEvent) {
        this.animationStateChanged.emit(event);
    }

    startExitAnimation() {
        this.animationState = 'leave';
        this.cd.detectChanges();
    }

    ngOnInit(): void {
        const state: GalleryHistoryState = this.location.getState() as GalleryHistoryState;
        state.gallery = 'isOpen';
        this.location.replaceState(this.location.path(), undefined, state);
        this.media = this.data.media ?? [];
        this.index = this.data.mediaIndex ?? 0;

        this.trackingService
            .marketingConsentGiven()
            .pipe(
                distinctUntilChanged((v) => v !== this.hasConsent),
                takeUntil(this.unsubscribe)
            )
            .subscribe((consent) => {
                this.hasConsent = consent;
                this.cd.markForCheck();
            });

        this.router.events.pipe(take(1), takeUntil(this.unsubscribe)).subscribe(() => {
            if (this.dialogRef?.componentInstance) {
                this.onClose();
            }
        });
    }

    ngAfterViewInit() {
        this.thumbsSwiper = new Swiper('.gallery-overlay__thumbs', {
            spaceBetween: 10,
            freeMode: true,
            watchSlidesProgress: true,
            initialSlide: this.index,
        });

        this.gallerySwiper = new Swiper('.product-gallery-swiper', {
            initialSlide: this.index,
            slidesPerView: 'auto',
            spaceBetween: 30,
            centeredSlides: true,
            autoHeight: false,
            keyboard: true,
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            pagination: {
                el: '.swiper-pagination',
                type: 'fraction',
            },
            lazy: {
                loadOnTransitionStart: true,
                loadPrevNext: false,
            },
            thumbs: {
                swiper: this.thumbsSwiper,
            },
        });
    }

    @HostListener('window:keyup.esc', ['$event'])
    onEscape() {
        const state: GalleryHistoryState = this.location.getState() as GalleryHistoryState;
        state.gallery = 'closed';
        this.location.replaceState(this.location.path(), undefined, state);
        this.onClose();
    }

    @HostListener('window:popstate', ['$event'])
    onClose() {
        this.dialogRef.close();
    }

    onShowCookieBanner() {
        this.trackingService.showCookieBanner();
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}

interface GalleryHistoryState {
    gallery?: 'isOpen' | 'closed';
}
